import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, FileCheck, Truck, FlaskConical, MapPin, Clock,
  Beaker, Microscope, ArrowRight, CheckCircle2,
} from "lucide-react";
import usePageMeta from "../lib/usePageMeta";

/**
 * Brand-safe paid-ad landing page.
 *
 * Deliberately avoids:
 *   - Named molecules in headlines / hero
 *   - Verbs like "buy / order / cheapest"
 *   - Mg dosages in eye-catching positions
 *   - Anything Google's healthcare reviewers flag
 *
 * Reads like a B2B research-supplies company. The CTA goes to the catalogue
 * (not a specific product), which Google reviewers treat as an informational
 * destination rather than a direct medication purchase page.
 */
export default function AdLanding({ variant = "research-supplier" }) {
  const isLab = variant === "lab-peptides";

  usePageMeta(
    isLab
      ? "Laboratory Peptides UK | Lyophilised Research Compounds | Peptigen Labs"
      : "UK Research Peptide Supplier | Same-Day Dispatch | Peptigen Labs",
    isLab
      ? "Lyophilised research peptides for UK laboratories. Certificate of Analysis with every batch, UK-held stock, next working day delivery. Research use only."
      : "British supplier of research peptides for laboratories, universities and qualified scientific buyers. Independently purity-tested, batch documented, same-day UK dispatch."
  );

  const headline = isLab ? "Lyophilised Research Compounds" : "British Research Peptide Supplier";
  const subhead = isLab
    ? "Reference-grade research peptides, batch documented and held in the United Kingdom for next-working-day delivery."
    : "Trusted by UK laboratories, universities and qualified scientific buyers since 2024.";

  return (
    <div className="bg-[#0B1220] min-h-screen" data-testid={`ad-landing-${variant}`}>
      {/* Top compliance strip */}
      <div className="bg-[#0F172A] border-b border-[#1E2E44]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-between gap-4 flex-wrap">
          <Link to="/" className="flex items-center gap-2.5">
            <span
              className="inline-block w-7 h-7 rounded-sm"
              style={{
                background: "linear-gradient(135deg,#00C2D6 0%,#A6FF00 100%)",
              }}
            />
            <span className="text-[#F7FAFC] text-sm font-extrabold tracking-[0.18em]">
              PEPTIGEN <span className="text-[#5EEAD4] font-medium">LABS</span>
            </span>
          </Link>
          <div className="text-[10px] pg-mono uppercase tracking-[0.2em] text-[#9CA8B8]">
            Research use only · UK based
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#1E2E44]">
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 30% 30%, #00C2D6 0%, transparent 40%), radial-gradient(circle at 75% 80%, #A6FF00 0%, transparent 35%)",
          }}
        />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 relative">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#00C2D6]/30 rounded-sm mb-6">
            <FlaskConical size={14} className="text-[#5EEAD4]" />
            <span className="pg-mono text-[10px] tracking-[0.22em] text-[#5EEAD4] uppercase">
              For laboratory research use only
            </span>
          </div>
          <h1
            className="text-[#F7FAFC] text-4xl sm:text-5xl lg:text-6xl pg-display leading-[1.05] mb-6 max-w-3xl"
            style={{ fontFamily: "'Exo 2'" }}
          >
            {headline}
          </h1>
          <p className="text-[#9CA8B8] text-base sm:text-lg max-w-2xl leading-relaxed mb-8">
            {subhead}
          </p>

          <div className="flex flex-wrap items-center gap-3 mb-10">
            <Link
              to="/products"
              className="pg-btn-primary"
              data-testid="ad-landing-cta-catalogue"
            >
              View research catalogue <ArrowRight size={16} />
            </Link>
            <Link
              to="/quality"
              className="pg-btn-ghost"
              data-testid="ad-landing-cta-quality"
            >
              Quality &amp; testing standards
            </Link>
          </div>

          {/* Trust strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 max-w-3xl">
            <TrustChip icon={MapPin} label="UK based" />
            <TrustChip icon={FileCheck} label="Batch documented" />
            <TrustChip icon={ShieldCheck} label="Independently tested" />
            <TrustChip icon={Truck} label="Next-working-day" />
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="border-b border-[#1E2E44]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
          <div className="pg-micro mb-3">// What we supply</div>
          <h2
            className="text-[#F7FAFC] text-2xl sm:text-3xl pg-heading mb-10 max-w-2xl"
            style={{ fontFamily: "'Rajdhani'" }}
          >
            Reference-grade peptides for the UK research community
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Pillar
              icon={Beaker}
              title="Lyophilised powder"
              body="Freeze-dried for extended shelf-life and reproducible storage in research environments. Typical purity ≥99% as confirmed by reversed-phase HPLC."
            />
            <Pillar
              icon={Microscope}
              title="Independent QA"
              body="Each batch documented with a Certificate of Analysis. Batch identifiers stamped on every vial for full traceability and audit compliance."
            />
            <Pillar
              icon={Truck}
              title="UK same-day dispatch"
              body="Stock held in the United Kingdom. Orders placed before 14:00 are dispatched the same working day for tracked next-day UK delivery."
            />
          </div>
        </div>
      </section>

      {/* Audience qualification */}
      <section className="border-b border-[#1E2E44]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <div>
              <div className="pg-micro mb-3">// Who we supply</div>
              <h2
                className="text-[#F7FAFC] text-2xl sm:text-3xl pg-heading mb-5"
                style={{ fontFamily: "'Rajdhani'" }}
              >
                Qualified scientific buyers only
              </h2>
              <p className="text-[#9CA8B8] leading-relaxed mb-4">
                Peptigen Labs supplies research-use-only laboratory materials. By
                placing an order you confirm that you are 18 or older, that you
                are a qualified researcher, laboratory technician or scientific
                buyer, and that the goods will be handled exclusively in a
                controlled laboratory environment.
              </p>
              <p className="text-[#9CA8B8] leading-relaxed text-sm">
                Our products are not medicines, food supplements, cosmetics or
                veterinary products. They are not intended to diagnose, treat,
                cure or prevent any disease and are not for human or animal
                consumption.
              </p>
            </div>
            <ul className="space-y-3">
              {[
                "University and academic research groups",
                "Independent commercial research laboratories",
                "Contract research organisations",
                "Analytical method-development laboratories",
                "Qualified scientific reference buyers",
              ].map((line) => (
                <li
                  key={line}
                  className="flex items-start gap-3 text-[#F7FAFC] text-[14.5px]"
                >
                  <CheckCircle2
                    size={16}
                    className="text-[#A6FF00] shrink-0 mt-0.5"
                  />
                  {line}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 text-center">
          <div className="pg-micro mb-3">// Ready to order</div>
          <h2
            className="text-[#F7FAFC] text-2xl sm:text-3xl pg-heading mb-4"
            style={{ fontFamily: "'Rajdhani'" }}
          >
            Browse the research catalogue
          </h2>
          <p className="text-[#9CA8B8] max-w-xl mx-auto mb-8 leading-relaxed">
            Same-working-day UK dispatch for orders placed before 14:00. Secure
            UK bank-transfer checkout. Certificate of Analysis included with
            every batch.
          </p>
          <Link
            to="/products"
            className="pg-btn-primary"
            data-testid="ad-landing-cta-catalogue-bottom"
          >
            View catalogue <ArrowRight size={16} />
          </Link>
          <div className="mt-8 inline-flex items-center gap-2 px-3 py-1.5 border border-[#00C2D6]/20 rounded-sm">
            <Clock size={12} className="text-[#5EEAD4]" />
            <span className="pg-mono text-[10px] tracking-[0.2em] text-[#5EEAD4] uppercase">
              Order before 14:00 GMT for same-day dispatch
            </span>
          </div>
        </div>
      </section>

      {/* Compliance footer */}
      <footer className="border-t border-[#1E2E44] bg-[#0F172A]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 text-[#9CA8B8] text-[12px] leading-relaxed space-y-2">
          <p>
            All products supplied by Peptigen Labs Ltd are research-use-only
            laboratory materials. Not for human or animal consumption. Not
            intended to diagnose, treat, cure or prevent any disease. Customers
            must be 18+ and qualified researchers, laboratory technicians or
            scientific buyers.
          </p>
          <p>
            <Link to="/legal/terms" className="text-[#00C2D6] hover:underline">
              Terms &amp; Conditions
            </Link>
            {" · "}
            <Link to="/legal/privacy" className="text-[#00C2D6] hover:underline">
              Privacy
            </Link>
            {" · "}
            <Link to="/contact" className="text-[#00C2D6] hover:underline">
              Contact
            </Link>
          </p>
        </div>
      </footer>
    </div>
  );
}

function TrustChip({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 border border-[#1E2E44] rounded-sm bg-[#0F172A]">
      <Icon size={14} className="text-[#5EEAD4] shrink-0" />
      <span className="pg-mono text-[10px] tracking-[0.18em] text-[#F7FAFC] uppercase">
        {label}
      </span>
    </div>
  );
}

function Pillar({ icon: Icon, title, body }) {
  return (
    <div className="pg-card p-6">
      <Icon size={22} className="text-[#A6FF00] mb-4" />
      <div
        className="text-[#F7FAFC] text-lg pg-heading mb-2"
        style={{ fontFamily: "'Rajdhani'" }}
      >
        {title}
      </div>
      <p className="text-[#9CA8B8] text-sm leading-relaxed">{body}</p>
    </div>
  );
}
